import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { MediaData, MediaFilters, MediaStatistics } from '../../types';
import apiService from '../../services/api';

interface MediaState {
  media: MediaData[];
  selectedMedia: MediaData | null;
  statistics: MediaStatistics | null;
  filters: MediaFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: MediaState = {
  media: [],
  selectedMedia: null,
  statistics: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchMedia = createAsyncThunk(
  'media/fetchMedia',
  async ({ page = 1, limit = 20, filters = {} }: { page?: number; limit?: number; filters?: MediaFilters }) => {
    const response = await apiService.getMedia(page, limit, filters);
    return response;
  }
);

export const fetchMediaItem = createAsyncThunk(
  'media/fetchMediaItem',
  async (mediaId: string) => {
    const response = await apiService.getMediaItem(mediaId);
    return response;
  }
);

export const createMedia = createAsyncThunk(
  'media/createMedia',
  async (mediaData: { file: File; isVideo: boolean; type: 'creation' | 'starting' | 'ending' }) => {
    const response = await apiService.createMedia(mediaData);
    return response;
  }
);

export const updateMedia = createAsyncThunk(
  'media/updateMedia',
  async ({ mediaId, mediaData }: { mediaId: string; mediaData: { type?: 'creation' | 'starting' | 'ending'; isVideo?: boolean } }) => {
    const response = await apiService.updateMedia(mediaId, mediaData);
    return { ...response, mediaId };
  }
);

export const deleteMedia = createAsyncThunk(
  'media/deleteMedia',
  async (mediaId: string) => {
    const response = await apiService.deleteMedia(mediaId);
    return { ...response, mediaId };
  }
);

export const fetchMediaStats = createAsyncThunk(
  'media/fetchMediaStats',
  async () => {
    const response = await apiService.getMediaStats();
    return response;
  }
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedMedia: (state, action: PayloadAction<MediaData | null>) => {
      state.selectedMedia = action.payload;
    },
    setFilters: (state, action: PayloadAction<MediaFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Media
      .addCase(fetchMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedia.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.media = action.payload.media;
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.total,
            pages: action.payload.pagination.pages,
          };
        }
      })
      .addCase(fetchMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch media';
      })

      // Fetch Media Item
      .addCase(fetchMediaItem.fulfilled, (state, action) => {
        state.selectedMedia = action.payload;
      })

      // Create Media
      .addCase(createMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMedia.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.media.unshift(action.payload.media);
          state.pagination.total += 1;
        }
      })
      .addCase(createMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create media';
      })

      // Update Media
      .addCase(updateMedia.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.media.findIndex(m => m.id === action.payload.mediaId);
          if (index !== -1) {
            state.media[index] = action.payload.media;
          }
          if (state.selectedMedia?.id === action.payload.mediaId) {
            state.selectedMedia = action.payload.media;
          }
        }
      })

      // Delete Media
      .addCase(deleteMedia.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.media = state.media.filter(m => m.id !== action.payload.mediaId);
          state.pagination.total -= 1;
          if (state.selectedMedia?.id === action.payload.mediaId) {
            state.selectedMedia = null;
          }
        }
      })

      // Fetch Media Stats
      .addCase(fetchMediaStats.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.statistics = action.payload.statistics;
        }
      });
  },
});

export const { clearError, setSelectedMedia, setFilters, clearFilters, setPage } = mediaSlice.actions;
export default mediaSlice.reducer;
